import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DateFilter from "../../../components/common/DateFilter";
import {
  getPaymentExceptionOverview,
  getOrganisation,
  getProvidersNames,
} from "../../../config/LiveApi";

// ---------- STYLES ----------
const styles = {
  container: {
    padding: "24px",
    background: "#f3f4f6",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
  },

  topSection: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "24px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
    marginBottom: "24px",
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    flexWrap: "wrap",
  },

  titleSection: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  heading: {
    margin: 0,
    fontSize: "30px",
    fontWeight: "700",
    color: "#111827",
    letterSpacing: "-0.5px",
  },

  subHeading: {
    margin: 0,
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
  },

  filtersWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },

  filterBox: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#4b5563",
  },

  select: {
    height: "46px",
    minWidth: "240px",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    padding: "0 14px",
    outline: "none",
    cursor: "pointer",
    background: "#ffffff",
    color: "#111827",
    fontWeight: "500",
    fontSize: "14px",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },

  errorText: {
    color: "#dc2626",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    padding: "12px 16px",
    borderRadius: "10px",
    marginBottom: "20px",
    fontSize: "14px",
    fontWeight: "500",
  },

  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "20px",
  },

  card: {
    background: "#ffffff",
    borderRadius: "18px",
    padding: "24px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
    transition: "all 0.2s ease",
  },

  cardTitle: {
    margin: 0,
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "600",
    marginBottom: "14px",
  },

  cardValue: {
    margin: 0,
    fontSize: "34px",
    fontWeight: "700",
    color: "#111827",
    letterSpacing: "-1px",
  },

  loader: {
    padding: "40px",
    textAlign: "center",
    fontSize: "18px",
    fontWeight: "600",
    color: "#6b7280",
  },
};

const PaymentExceptionOverview = () => {
  const token = useSelector((state) => state.userReducer.token);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [organisations, setOrganisations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchOrganisations();
    fetchProviders();
  }, []);
  useEffect(() => {
    if (!startDate || !endDate) return;

    handleApply();
  }, [startDate, endDate, selectedOrg, selectedProvider]);

  const fetchOrganisations = async () => {
    try {
      const response = await getOrganisation(token);

      setOrganisations(response?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await getProvidersNames(token);

      const providerData = response || [];

      const uniqueProviders = [
        ...new Map(
          providerData.map((item) => [
            item.label,
            {
              label: item.label,
            },
          ]),
        ).values(),
      ];

      setProviders(uniqueProviders);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchOverview = async (customParams = {}) => {
    try {
      setLoading(true);
      setError("");

      const response = await getPaymentExceptionOverview(token, customParams);

      setOverview(response);
    } catch (err) {
      setError(err.message || "Failed to fetch overview");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (fromDate, toDate) => {
    setStartDate(fromDate);
    setEndDate(toDate);
  };

  const handleApply = async () => {
    const params = {
      fromDate: startDate,
      toDate: endDate,
      orgName: selectedOrg || "",
      providerName: selectedProvider || "",
    };

    await fetchOverview(params);
  };

  const cards = [
    {
      title: "Total Exceptions",
      value: overview?.totalExceptions || 0,
    },
    {
      title: "Total Amount",
      value: `₹ ${overview?.totalAmount || 0}`,
    },
    {
      title: "Pending Refunds",
      value: overview?.pendingRefunds || 0,
    },
    {
      title: "Success Refunds",
      value: overview?.successRefunds || 0,
    },
    {
      title: "Failed Refunds",
      value: overview?.failedRefunds || 0,
    },
  ];

  return (
    <div style={styles.container}>
      {loading && <div style={styles.loader}>Loading overview...</div>}
      <div style={styles.topSection}>
        <div style={styles.headerRow}>
          <div style={styles.titleSection}>
            <h2 style={styles.heading}>Payment Exception Overview</h2>

            <p style={styles.subHeading}>
              Monitor refund performance, exception counts and payment health
            </p>
          </div>

          <div style={styles.filtersWrapper}>
            {/* Organisation */}
            <div style={styles.filterBox}>
              <label style={styles.label}>Organisation</label>

              <select
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                style={styles.select}
              >
                <option value="">All Organisations</option>

                {organisations.map((org, index) => (
                  <option key={index} value={org?.name || org?.orgName}>
                    {org?.name || org?.orgName}
                  </option>
                ))}
              </select>
            </div>

            {/* Provider */}
            <div style={styles.filterBox}>
              <label style={styles.label}>Provider</label>

              <select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                style={styles.select}
              >
                <option value="">All Providers</option>

                {providers.map((provider, index) => (
                  <option key={index} value={provider?.label}>
                    {provider?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <DateFilter onDateChange={handleDateChange} />
      </div>

      {/* Error */}
      {error && <div style={styles.errorText}>{error}</div>}

      {/* Cards */}
      <div style={styles.cardsGrid}>
        {cards.map((card, index) => (
          <div key={index} style={styles.card}>
            <p style={styles.cardTitle}>{card.title}</p>

            <h2 style={styles.cardValue}>{card.value}</h2>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentExceptionOverview;
